import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { fetchCases } from '../../api';
import { 
  defaultPositiveVerificationQuestions, 
  defaultNegativeVerificationQuestions,
  defaultPositiveRequiredImages,
  defaultNegativeRequiredImages,
  CASE_STATUSES,
  STATUS_CONFIG
} from '../../data/mockData';
import { 
  Search, 
  Filter, 
  CheckCircle, 
  XCircle, 
  Plus, 
  Trash2, 
  Camera, 
  Upload, 
  Save, 
  FileCheck, 
  Eye, 
  ArrowLeft, 
  AlertCircle, 
  Calendar, 
  ChevronRight, 
  Grid, 
  List, 
  Building, 
  Phone, 
  Mail, 
  FileText, 
  Check, 
  X,
  MapPin,
  Map,
  MapPinIcon,
  RefreshCw,
  Clock,
  User,
  ShieldCheck,
  ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';

export const ManualVerification = () => {
  const { statusGroup } = useParams();
  const navigate = useNavigate();
  const { currentUserId, currentRole, addNotification } = useApp();

  // Screen State: 1 = Listing, 2 = Details, 3 = Form
  const [currentScreen, setCurrentScreen] = useState(1);
  const [selectedCase, setSelectedCase] = useState(null);
  
  // Case listing states (loaded into local state for interactive demo)
  const [casesList, setCasesList] = useState([]);
  const [viewMode, setViewMode] = useState('card'); // 'card' or 'table'
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filterClient, setFilterClient] = useState('');
  const [filterProduct, setFilterProduct] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterDate, setFilterDate] = useState('');
  
  // Form Screen States
  const [outcomeMode, setOutcomeMode] = useState('positive'); // 'positive' or 'negative'
  const [formAnswers, setFormAnswers] = useState({});
  const [uploadedImages, setUploadedImages] = useState([]);
  const [feRemarks, setFeRemarks] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  
  // QC States
  const [qcStatus, setQcStatus] = useState('approve'); // 'approve' or 'reject'
  const [qcRemarks, setQcRemarks] = useState('');

  // Drafts Persistent State
  const [draftsMap, setDraftsMap] = useState(() => {
    try {
      const stored = localStorage.getItem('manual_verification_drafts');
      return stored ? JSON.parse(stored) : {};
    } catch (e) {
      return {};
    }
  });

  // Geotagging Modal States
  const [geotagModalOpen, setGeotagModalOpen] = useState(false);
  const [activeGeoImageIdx, setActiveGeoImageIdx] = useState(null);
  const [tempLat, setTempLat] = useState('23.0225');
  const [tempLng, setTempLng] = useState('72.5714');
  
  // Geotag coordinates offset simulator for clicking on map placeholder grid
  const handleMapGridClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left; // x position within the element.
    const y = e.clientY - rect.top;  // y position within the element.
    
    // Simulate latitude and longitude shifts based on click offset
    const baseLat = 23.0225;
    const baseLng = 72.5714;
    const computedLat = (baseLat + (100 - y) * 0.00015).toFixed(6);
    const computedLng = (baseLng + (x - 150) * 0.00015).toFixed(6);
    
    setTempLat(computedLat);
    setTempLng(computedLng);
    toast.info(`Pin placed at: Lat ${computedLat}, Lng ${computedLng}`);
  };

  // Sync Draft changes to LocalStorage
  const saveDraftToStorage = (updatedDrafts) => {
    setDraftsMap(updatedDrafts);
    try {
      localStorage.setItem('manual_verification_drafts', JSON.stringify(updatedDrafts));
    } catch (e) {
      console.error(e);
    }
  };

  // Populate cases list from live database on load
  useEffect(() => {
    let isMounted = true;
    fetchCases()
      .then(res => {
        if (!isMounted) return;
        setCasesList(Array.isArray(res) ? res : []);
      })
      .catch(err => {
        if (!isMounted) return;
        console.warn('Failed to fetch cases:', err);
        setCasesList([]);
      });
    return () => { isMounted = false; };
  }, [currentUserId, currentRole]);

  // Sync screen state to route/submenu changes
  useEffect(() => {
    setCurrentScreen(1);
    setSelectedCase(null);
  }, [statusGroup]);

  // Clean filters on status change
  const clearFilters = () => {
    setSearchQuery('');
    setFilterClient('');
    setFilterProduct('');
    setFilterType('');
    setFilterDate('');
  };

  // Categorize cases into sidebar groups
  const categorizedCases = useMemo(() => {
    return casesList.map(c => {
      const draft = draftsMap[c.id];
      if (draft) {
        return { ...c, isDraft: true };
      }
      return c;
    });
  }, [casesList, draftsMap]);

  const filteredCases = useMemo(() => {
    return categorizedCases.filter(c => {
      // 1. Group filtering logic
      if (statusGroup === 'pending') {
        if (c.status !== CASE_STATUSES.CREATED && c.status !== CASE_STATUSES.ASSIGNED) return false;
        if (c.isDraft) return false;
      } else if (statusGroup === 'in-progress') {
        if (c.status !== CASE_STATUSES.IN_PROGRESS) return false;
        if (c.isDraft) return false;
      } else if (statusGroup === 'completed') {
        const completedStatuses = [
          CASE_STATUSES.COMPLETED, 
          CASE_STATUSES.VERIFIED, 
          CASE_STATUSES.QC_APPROVED, 
          CASE_STATUSES.REPORT_SENT,
          CASE_STATUSES.QC_PENDING
        ];
        if (!completedStatuses.includes(c.status)) return false;
        if (c.isDraft) return false;
      } else if (statusGroup === 'rejected') {
        if (c.status !== CASE_STATUSES.QC_REJECTED && c.status !== CASE_STATUSES.REVISIT) return false;
        if (c.isDraft) return false;
      } else if (statusGroup === 'drafts') {
        if (!c.isDraft) return false;
      }

      // 2. Multi-select filtering logic
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesId = c.id.toLowerCase().includes(query) || (c.refNumber && c.refNumber.toLowerCase().includes(query));
        const matchesName = c.applicantName && c.applicantName.toLowerCase().includes(query);
        const matchesAddress = c.address && c.address.toLowerCase().includes(query);
        if (!matchesId && !matchesName && !matchesAddress) return false;
      }

      if (filterClient && c.client && c.client.name !== filterClient) return false;
      if (filterProduct && c.product && c.product.name !== filterProduct) return false;
      if (filterType && c.verificationType !== filterType) return false;
      
      if (filterDate) {
        const cDate = c.createdAt ? c.createdAt.substring(0, 10) : '';
        if (cDate !== filterDate) return false;
      }

      return true;
    });
  }, [categorizedCases, statusGroup, searchQuery, filterClient, filterProduct, filterType, filterDate]);

  // Unique clients and products list for dropdown filters
  const clientsList = useMemo(() => {
    const clients = new Set(casesList.map(c => c.client?.name).filter(Boolean));
    return Array.from(clients);
  }, [casesList]);

  const productsList = useMemo(() => {
    const products = new Set(casesList.map(c => c.product?.name).filter(Boolean));
    return Array.from(products);
  }, [casesList]);

  const verifTypesList = useMemo(() => {
    const types = new Set(casesList.map(c => c.verificationType).filter(Boolean));
    return Array.from(types);
  }, [casesList]);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;
  const paginatedCases = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredCases.slice(start, start + itemsPerPage);
  }, [filteredCases, currentPage]);

  const totalPages = Math.ceil(filteredCases.length / itemsPerPage);

  // Switch screens
  const handleSelectCase = (c) => {
    setSelectedCase(c);
    setCurrentScreen(2); // Go to Case Details
  };

  // Start Form Screen
  const handleStartVerification = () => {
    // Check if there is a draft saved for this case
    const savedDraft = draftsMap[selectedCase.id];
    if (savedDraft) {
      setOutcomeMode(savedDraft.outcomeMode || 'positive');
      setFormAnswers(savedDraft.formAnswers || {});
      setUploadedImages(savedDraft.uploadedImages || []);
      setFeRemarks(savedDraft.feRemarks || '');
      setInternalNotes(savedDraft.internalNotes || '');
      toast.info('Restored form details from saved draft.');
    } else {
      // Initialize empty form state
      setOutcomeMode('positive');
      setFormAnswers({});
      
      // Initialize uploaded images list based on verificationType defaults
      const requiredImages = defaultPositiveRequiredImages.map(img => ({
        label: img.label,
        required: img.required,
        geoTagRequired: img.geoTag,
        file: null,
        previewUrl: '',
        lat: '23.0225',
        lng: '72.5714',
        timestamp: '',
        address: selectedCase.address || 'Address location'
      }));
      setUploadedImages(requiredImages);
      setFeRemarks('');
      setInternalNotes('');
    }
    
    // Set QC default comments
    setQcRemarks('');
    setQcStatus('approve');

    setCurrentScreen(3); // Go to Dynamic Form
  };

  // Outcome Toggle change
  const handleOutcomeChange = (mode) => {
    setOutcomeMode(mode);
    
    // Save draft or swap default image slots
    const defaultImages = mode === 'positive' ? defaultPositiveRequiredImages : defaultNegativeRequiredImages;
    const requiredImages = defaultImages.map(img => ({
      label: img.label,
      required: img.required,
      geoTagRequired: img.geoTag,
      file: null,
      previewUrl: '',
      lat: '23.0225',
      lng: '72.5714',
      timestamp: '',
      address: selectedCase.address || 'Address location'
    }));
    setUploadedImages(requiredImages);
    setFormAnswers({});
    toast.success(`Swapped to ${mode === 'positive' ? 'Positive Outcome Form' : 'Negative Outcome Form'}`);
  };

  // Question lists based on selected outcome
  const activeQuestions = useMemo(() => {
    if (outcomeMode === 'positive') {
      return defaultPositiveVerificationQuestions;
    } else {
      return defaultNegativeVerificationQuestions;
    }
  }, [outcomeMode]);

  // Handle Form Input Value Changes
  const handleInputChange = (questionId, value) => {
    setFormAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  // Simulated Camera Capture
  const handleCameraCapture = (index) => {
    // Curated Unsplash images for realistic demo
    const positivePics = [
      'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=400',
      'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=400',
      'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=400',
      'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=400'
    ];
    
    const randomPic = positivePics[Math.floor(Math.random() * positivePics.length)];
    const now = new Date();
    const timestamp = now.toLocaleString();
    
    // Capture details
    const updatedImages = [...uploadedImages];
    updatedImages[index] = {
      ...updatedImages[index],
      previewUrl: randomPic,
      timestamp: timestamp,
      lat: '23.0225',
      lng: '72.5714',
      address: selectedCase.address || '123, Verification Location'
    };
    
    setUploadedImages(updatedImages);
    toast.success(`Simulated photo captured with GPS Telemetry & Watermark!`);
  };

  // Simulated File Upload
  const handleFileUpload = (index, e) => {
    const file = e.target.files[0];
    if (file) {
      const now = new Date();
      const timestamp = now.toLocaleString();
      const previewUrl = URL.createObjectURL(file);
      
      const updatedImages = [...uploadedImages];
      updatedImages[index] = {
        ...updatedImages[index],
        file: file,
        previewUrl: previewUrl,
        timestamp: timestamp,
        lat: '23.0225',
        lng: '72.5714',
        address: selectedCase.address || '123, Verification Location'
      };
      setUploadedImages(updatedImages);
      toast.success(`Uploaded ${file.name} successfully!`);
    }
  };

  const removeImage = (index) => {
    const updatedImages = [...uploadedImages];
    updatedImages[index] = {
      ...updatedImages[index],
      file: null,
      previewUrl: '',
      timestamp: ''
    };
    setUploadedImages(updatedImages);
    toast.info('Image removed.');
  };

  // Open Geotag Editor Modal
  const openGeotagModal = (index) => {
    setActiveGeoImageIdx(index);
    setTempLat(uploadedImages[index].lat || '23.0225');
    setTempLng(uploadedImages[index].lng || '72.5714');
    setGeotagModalOpen(true);
  };

  // Save Custom Geotag Details
  const saveGeotagDetails = () => {
    if (activeGeoImageIdx !== null) {
      const updatedImages = [...uploadedImages];
      updatedImages[activeGeoImageIdx] = {
        ...updatedImages[activeGeoImageIdx],
        lat: tempLat,
        lng: tempLng
      };
      setUploadedImages(updatedImages);
      setGeotagModalOpen(false);
      toast.success(`Updated geotag coordinate specs for ${uploadedImages[activeGeoImageIdx].label}!`);
    }
  };

  // Save Draft Handler
  const handleSaveDraft = () => {
    const draftData = {
      caseId: selectedCase.id,
      outcomeMode,
      formAnswers,
      uploadedImages,
      feRemarks,
      internalNotes,
      savedAt: new Date().toLocaleString()
    };
    
    const updatedDrafts = { ...draftsMap, [selectedCase.id]: draftData };
    saveDraftToStorage(updatedDrafts);
    
    // Update local status of case to show draft indicators
    toast.success(`Draft saved successfully for Lead ${selectedCase.id}`);
  };

  // Submit to QC Handler (or Approve/Reject if logged in as QC)
  const handleSubmitVerification = () => {
    // If FE/Vendor role, submit goes to QC pending
    if (currentRole === 'quality_checker') {
      // QC specific logic
      const finalStatus = qcStatus === 'approve' ? CASE_STATUSES.QC_APPROVED : CASE_STATUSES.QC_REJECTED;
      
      // Update case list status
      const updatedCases = casesList.map(c => {
        if (c.id === selectedCase.id) {
          return {
            ...c,
            status: finalStatus,
            remarks: {
              ...c.remarks,
              qcRemark: qcRemarks
            },
            updatedAt: new Date().toISOString()
          };
        }
        return c;
      });
      setCasesList(updatedCases);
      
      // Remove draft if any
      const updatedDrafts = { ...draftsMap };
      delete updatedDrafts[selectedCase.id];
      saveDraftToStorage(updatedDrafts);

      addNotification({
        title: qcStatus === 'approve' ? 'Verification Case Approved' : 'Verification Case Rejected',
        message: `Case ${selectedCase.id} has been ${qcStatus === 'approve' ? 'approved' : 'rejected'} by Sneha Reddy (QC).`,
        type: qcStatus === 'approve' ? 'success' : 'danger',
        targetRole: 'admin'
      });

      toast.success(`Case ${selectedCase.id} successfully review resolved: ${qcStatus === 'approve' ? 'APPROVED' : 'REJECTED'}`);
      setCurrentScreen(1);
      return;
    }

    // Standard Submit to QC
    // Check if required images are captured
    const missingImages = uploadedImages.filter(img => img.required && !img.previewUrl);
    if (missingImages.length > 0) {
      toast.error(`Please provide required images: ${missingImages.map(m => m.label).join(', ')}`);
      return;
    }

    // Check if required form questions are answered
    const missingQuestions = activeQuestions.filter(q => q.required && !formAnswers[q.id]);
    if (missingQuestions.length > 0) {
      toast.error(`Please answer required fields: ${missingQuestions.map(m => m.fieldLabel).join(', ')}`);
      return;
    }

    const updatedCases = casesList.map(c => {
      if (c.id === selectedCase.id) {
        // Build mock answers block
        const answersBlock = activeQuestions.map(q => ({
          question: q.fieldLabel,
          answer: formAnswers[q.id] || 'N/A'
        }));

        return {
          ...c,
          status: CASE_STATUSES.QC_PENDING,
          verificationStatus: outcomeMode,
          answers: answersBlock,
          remarks: {
            ...c.remarks,
            feRemark: feRemarks
          },
          verifiedImages: uploadedImages.filter(img => img.previewUrl).map(img => ({
            label: img.label,
            url: img.previewUrl,
            latitude: parseFloat(img.lat),
            longitude: parseFloat(img.lng),
            timestamp: img.timestamp,
            address: img.address
          })),
          mapCoords: {
            latitude: parseFloat(uploadedImages[0]?.lat || '23.0225'),
            longitude: parseFloat(uploadedImages[0]?.lng || '72.5714')
          },
          updatedAt: new Date().toISOString()
        };
      }
      return c;
    });

    setCasesList(updatedCases);

    // Delete saved drafts for this case
    const updatedDrafts = { ...draftsMap };
    delete updatedDrafts[selectedCase.id];
    saveDraftToStorage(updatedDrafts);

    addNotification({
      title: 'Manual Verification Form Submitted',
      message: `Verification form for Case ${selectedCase.id} has been submitted for QC approval by Vikram Singh.`,
      type: 'info',
      targetRole: 'quality_checker'
    });

    toast.success(`Verification completed! Lead ${selectedCase.id} submitted to Quality Checking dashboard.`);
    setCurrentScreen(1);
  };

  // Re-open case by Admin
  const handleAdminReopenCase = () => {
    const updatedCases = casesList.map(c => {
      if (c.id === selectedCase.id) {
        return {
          ...c,
          status: CASE_STATUSES.IN_PROGRESS,
          updatedAt: new Date().toISOString()
        };
      }
      return c;
    });
    setCasesList(updatedCases);
    toast.success(`Admin Override: Case reopened and set to In-Progress status.`);
    setCurrentScreen(1);
  };

  // Re-assign Case (Manager/Admin Only)
  const handleAssignCase = (userId) => {
    const updatedCases = casesList.map(c => {
      if (c.id === selectedCase.id) {
        return {
          ...c,
          status: CASE_STATUSES.IN_PROGRESS,
          updatedAt: new Date().toISOString()
        };
      }
      return c;
    });
    setCasesList(updatedCases);
    toast.success(`Case successfully assigned/scheduled for field operations.`);
  };

  return (
    <div className="crm-content p-6 max-w-7xl mx-auto space-y-6 bg-slate-50 min-h-screen">
      
      {/* ------------------ SCREEN 1: CASE LISTING DASHBOARD ------------------ */}
      {currentScreen === 1 && (
        <div className="space-y-6">
          {/* Header HUD Banner */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
            <div>
              <h1 className="text-2xl font-bold text-slate-800 tracking-tight capitalize">
                Manual Verification Portal - {statusGroup?.replace('-', ' ')}
              </h1>
              <p className="text-sm text-slate-500 mt-1">
                Real-time manual field reporting, dynamic outcomes submission pipeline, and EXIF geotag telemetry engine.
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Quick actions panel */}
              <button 
                onClick={clearFilters}
                className="flex items-center gap-2 px-3 py-2 border border-slate-200 text-slate-600 hover:text-slate-900 rounded-lg text-xs font-semibold hover:bg-slate-50 transition-all"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Clear Filters
              </button>
              <div className="flex items-center border border-slate-200 rounded-lg overflow-hidden bg-slate-100 p-0.5">
                <button
                  onClick={() => setViewMode('card')}
                  className={`p-1.5 rounded-md transition-all ${viewMode === 'card' ? 'bg-white shadow-sm text-blue-600' : 'text-slate-400 hover:text-slate-700'}`}
                  title="Card View"
                >
                  <Grid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('table')}
                  className={`p-1.5 rounded-md transition-all ${viewMode === 'table' ? 'bg-white shadow-sm text-blue-600' : 'text-slate-400 hover:text-slate-700'}`}
                  title="Spreadsheet Table View"
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Quick Stats Summary bar */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {[
              { label: 'Scoped Total', val: filteredCases.length, color: 'border-l-blue-600 bg-blue-50/50' },
              { label: 'Pending Queue', val: categorizedCases.filter(c => c.status === 'created' || c.status === 'assigned').length, color: 'border-l-amber-500 bg-amber-50/30' },
              { label: 'In Progress Ops', val: categorizedCases.filter(c => c.status === 'in_progress').length, color: 'border-l-indigo-500 bg-indigo-50/30' },
              { label: 'Completed Review', val: categorizedCases.filter(c => ['completed', 'qc_approved', 'report_sent'].includes(c.status)).length, color: 'border-l-emerald-500 bg-emerald-50/30' },
              { label: 'Uncommitted Drafts', val: Object.keys(draftsMap).length, color: 'border-l-rose-500 bg-rose-50/30' },
            ].map((stat, idx) => (
              <div key={idx} className={`p-4 rounded-xl border border-slate-200 bg-white border-l-4 ${stat.color} shadow-sm flex flex-col justify-between`}>
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{stat.label}</span>
                <span className="text-2xl font-bold text-slate-800 mt-2">{stat.val}</span>
              </div>
            ))}
          </div>

          {/* Filters Bar Section */}
          <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm space-y-4">
            <div className="flex items-center gap-2 border-b border-slate-100 pb-3 mb-2">
              <Filter className="w-4 h-4 text-blue-600" />
              <span className="font-semibold text-sm text-slate-700">Advanced Search & Multi-Filters Panel</span>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
              {/* Text Search */}
              <div className="relative">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search Case, Applicant, City..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 pr-3 py-2 w-full border border-slate-200 rounded-lg text-xs outline-none bg-slate-50 focus:bg-white focus:ring-1 focus:ring-blue-500 text-slate-700 font-medium"
                />
              </div>

              {/* Clients Filter */}
              <select
                value={filterClient}
                onChange={(e) => setFilterClient(e.target.value)}
                className="px-3 py-2 w-full border border-slate-200 rounded-lg text-xs outline-none bg-slate-50 focus:bg-white text-slate-600 font-medium cursor-pointer"
              >
                <option value="">All Clients</option>
                {clientsList.map((client, idx) => (
                  <option key={idx} value={client}>{client}</option>
                ))}
              </select>

              {/* Products Filter */}
              <select
                value={filterProduct}
                onChange={(e) => setFilterProduct(e.target.value)}
                className="px-3 py-2 w-full border border-slate-200 rounded-lg text-xs outline-none bg-slate-50 focus:bg-white text-slate-600 font-medium cursor-pointer"
              >
                <option value="">All Products</option>
                {productsList.map((prod, idx) => (
                  <option key={idx} value={prod}>{prod}</option>
                ))}
              </select>

              {/* Verification Type Filter */}
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="px-3 py-2 w-full border border-slate-200 rounded-lg text-xs outline-none bg-slate-50 focus:bg-white text-slate-600 font-medium cursor-pointer"
              >
                <option value="">All Verification Types</option>
                {verifTypesList.map((type, idx) => (
                  <option key={idx} value={type}>{type}</option>
                ))}
              </select>

              {/* Date Filter */}
              <input
                type="date"
                value={filterDate}
                onChange={(e) => setFilterDate(e.target.value)}
                className="px-3 py-2 w-full border border-slate-200 rounded-lg text-xs outline-none bg-slate-50 focus:bg-white text-slate-600 font-medium cursor-pointer"
              />
            </div>
          </div>

          {/* Listing Grid / Spreadsheet Table */}
          {filteredCases.length === 0 ? (
            <div className="bg-white p-16 rounded-xl border border-slate-200 shadow-sm text-center">
              <AlertCircle className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <h3 className="text-base font-bold text-slate-700">No active verification leads found</h3>
              <p className="text-slate-400 text-xs mt-1 max-w-md mx-auto">
                No cases matched your selected status queue or custom filter choices. Clear search values to refresh.
              </p>
            </div>
          ) : viewMode === 'card' ? (
            /* Card Grid View */
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {paginatedCases.map((c) => {
                const isCaseDraft = !!draftsMap[c.id];
                const statusTheme = STATUS_CONFIG[c.status] || { label: c.status, color: 'neutral' };
                
                return (
                  <div 
                    key={c.id}
                    className="bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-all flex flex-col justify-between overflow-hidden relative group"
                  >
                    {/* Draft Indicator Ribbon */}
                    {isCaseDraft && (
                      <div className="absolute top-0 right-0 bg-rose-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-bl-lg shadow-sm z-10">
                        DRAFT SAVED
                      </div>
                    )}
                    
                    {/* Top Content */}
                    <div className="p-5 space-y-4">
                      {/* Badge / Metadata header */}
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-bold tracking-widest text-blue-600 uppercase">
                          {c.id}
                        </span>
                        
                        <div className="flex items-center gap-1.5">
                          {/* Priority Badge */}
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                            c.priority === 'high' ? 'bg-red-50 text-red-600 border border-red-100' :
                            c.priority === 'medium' ? 'bg-amber-50 text-amber-600 border border-amber-100' :
                            'bg-slate-50 text-slate-600 border border-slate-100'
                          }`}>
                            {c.priority}
                          </span>
                          
                          {/* Status Badge */}
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase ${
                            statusTheme.color === 'success' ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' :
                            statusTheme.color === 'warning' ? 'bg-amber-50 text-amber-600 border border-amber-100' :
                            statusTheme.color === 'danger' ? 'bg-red-50 text-red-600 border border-red-100' :
                            statusTheme.color === 'info' ? 'bg-blue-50 text-blue-600 border border-blue-100' :
                            'bg-slate-50 text-slate-600 border border-slate-100'
                          }`}>
                            {statusTheme.label}
                          </span>
                        </div>
                      </div>

                      {/* Main client & applicant block */}
                      <div>
                        <h3 className="text-base font-bold text-slate-800">{c.applicantName}</h3>
                        <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-1">
                          <Building className="w-3.5 h-3.5 text-slate-400" />
                          <span>{c.client?.name || 'Client NA'}</span>
                          <span className="text-slate-300">•</span>
                          <span>{c.product?.name || 'Product NA'}</span>
                        </div>
                      </div>

                      {/* Details specs */}
                      <div className="space-y-2 border-t border-slate-100 pt-3">
                        <div className="flex justify-between items-start text-xs">
                          <span className="text-slate-400">Address:</span>
                          <span className="text-slate-600 font-medium text-right max-w-[180px] truncate" title={c.address}>
                            {c.address}
                          </span>
                        </div>
                        <div className="flex justify-between items-center text-xs">
                          <span className="text-slate-400">Type:</span>
                          <span className="text-blue-600 font-semibold">{c.verificationType || 'Residence Verification'}</span>
                        </div>
                        <div className="flex justify-between items-center text-xs">
                          <span className="text-slate-400">Due Date:</span>
                          <span className="text-slate-600 font-medium flex items-center gap-1">
                            <Calendar className="w-3 h-3 text-slate-400" />
                            {c.dueDate ? c.dueDate.substring(0, 10) : 'N/A'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Bottom Actions footer */}
                    <div className="bg-slate-50 border-t border-slate-100 p-3 px-5 flex items-center justify-between">
                      <span className="text-[10px] text-slate-400 font-semibold">
                        Assign: {c.createdAt ? c.createdAt.substring(0, 10) : 'N/A'}
                      </span>
                      
                      <button
                        onClick={() => handleSelectCase(c)}
                        className="flex items-center gap-1.5 text-xs font-bold text-blue-600 hover:text-blue-800 transition-colors py-1 px-3 bg-white border border-slate-200 hover:border-slate-300 rounded-lg shadow-sm"
                      >
                        {isCaseDraft ? 'Continue Verification' : 'Select Case'}
                        <ChevronRight className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            /* Table View Mode */
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200 text-xs font-bold text-slate-500 uppercase tracking-wider">
                      <th className="p-4 pl-6">Case ID</th>
                      <th className="p-4">Applicant</th>
                      <th className="p-4">Client</th>
                      <th className="p-4">Verification Type</th>
                      <th className="p-4">Address</th>
                      <th className="p-4 text-center">Priority</th>
                      <th className="p-4">Due Date</th>
                      <th className="p-4">Status</th>
                      <th className="p-4 pr-6 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-xs">
                    {paginatedCases.map((c) => {
                      const isCaseDraft = !!draftsMap[c.id];
                      const statusTheme = STATUS_CONFIG[c.status] || { label: c.status, color: 'neutral' };
                      
                      return (
                        <tr key={c.id} className="hover:bg-slate-50/50 transition-colors group">
                          <td className="p-4 pl-6 font-bold text-slate-700">
                            <div className="flex items-center gap-1.5">
                              {c.id}
                              {isCaseDraft && (
                                <span className="bg-rose-50 text-rose-600 text-[8px] font-extrabold px-1 py-0.5 rounded border border-rose-100">
                                  DRAFT
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="p-4 font-bold text-slate-800">{c.applicantName}</td>
                          <td className="p-4 text-slate-600">{c.client?.name || 'N/A'}</td>
                          <td className="p-4 text-blue-600 font-semibold">{c.verificationType || 'Residence'}</td>
                          <td className="p-4 text-slate-500 max-w-[200px] truncate" title={c.address}>{c.address}</td>
                          <td className="p-4 text-center">
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                              c.priority === 'high' ? 'bg-red-50 text-red-600 border border-red-100' :
                              c.priority === 'medium' ? 'bg-amber-50 text-amber-600 border border-amber-100' :
                              'bg-slate-50 text-slate-600 border border-slate-100'
                            }`}>
                              {c.priority}
                            </span>
                          </td>
                          <td className="p-4 text-slate-500 font-medium">{c.dueDate ? c.dueDate.substring(0, 10) : 'N/A'}</td>
                          <td className="p-4">
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase ${
                              statusTheme.color === 'success' ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' :
                              statusTheme.color === 'warning' ? 'bg-amber-50 text-amber-600 border border-amber-100' :
                              statusTheme.color === 'danger' ? 'bg-red-50 text-red-600 border border-red-100' :
                              statusTheme.color === 'info' ? 'bg-blue-50 text-blue-600 border border-blue-100' :
                              'bg-slate-50 text-slate-600 border border-slate-100'
                            }`}>
                              {statusTheme.label}
                            </span>
                          </td>
                          <td className="p-4 pr-6 text-right">
                            <button
                              onClick={() => handleSelectCase(c)}
                              className="text-xs font-bold text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100/50 py-1.5 px-3 rounded-lg border border-transparent hover:border-blue-200 transition-all inline-flex items-center gap-1"
                            >
                              Select
                              <ChevronRight className="w-3 h-3" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between bg-white px-6 py-4 rounded-xl border border-slate-200 shadow-sm text-xs">
              <span className="text-slate-500 font-medium">
                Showing page <strong className="text-slate-800">{currentPage}</strong> of <strong className="text-slate-800">{totalPages}</strong> ({filteredCases.length} total cases)
              </span>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 border border-slate-200 rounded-lg hover:bg-slate-50 text-slate-600 disabled:opacity-50 disabled:cursor-not-allowed font-semibold transition-all"
                >
                  Previous
                </button>
                {Array.from({ length: totalPages }).map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentPage(idx + 1)}
                    className={`w-8 h-8 rounded-lg font-bold border transition-all ${currentPage === idx + 1 ? 'bg-blue-600 text-white border-blue-600 shadow-sm shadow-blue-100' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                  >
                    {idx + 1}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1.5 border border-slate-200 rounded-lg hover:bg-slate-50 text-slate-600 disabled:opacity-50 disabled:cursor-not-allowed font-semibold transition-all"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}


      {/* ------------------ SCREEN 2: CASE DETAILS & TELEMETRY HUD ------------------ */}
      {currentScreen === 2 && selectedCase && (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
          
          {/* Top Control Header Panel */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setCurrentScreen(1)}
                className="p-2 border border-slate-200 rounded-lg text-slate-500 hover:text-slate-800 hover:bg-slate-50 transition-colors"
                title="Go Back to List"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-lg font-extrabold text-slate-800">
                    Lead Specifications Details: {selectedCase.id}
                  </h1>
                  {draftsMap[selectedCase.id] && (
                    <span className="bg-rose-50 border border-rose-100 text-rose-600 text-[9px] font-bold px-2 py-0.5 rounded-md shadow-sm">
                      DRAFT PENDING
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-400 mt-0.5">
                  Reference: {selectedCase.refNumber || 'LD-2026-00001'} | Scoped under {selectedCase.client?.name || 'ICICI Bank'} mapping.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* Action: Start Verification Form */}
              <button
                onClick={handleStartVerification}
                className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-bold shadow-sm shadow-blue-100 transition-colors"
              >
                <FileText className="w-3.5 h-3.5" />
                {draftsMap[selectedCase.id] ? 'Continue Form Verification' : 'Start Verification Form'}
              </button>

              {/* Action: Re-assign / Edit Assignments (Only Admin / Manager roles) */}
              {(currentRole === 'admin' || currentRole === 'manager') && (
                <button
                  onClick={() => handleAssignCase('USR005')}
                  className="px-3 py-2 border border-slate-200 hover:bg-slate-50 text-slate-600 font-bold rounded-lg text-xs transition-colors"
                >
                  Reassign Field Officer
                </button>
              )}

              {/* Admin Re-open Case Override Option */}
              {currentRole === 'admin' && ['completed', 'qc_approved', 'qc_rejected'].includes(selectedCase.status) && (
                <button
                  onClick={handleAdminReopenCase}
                  className="px-3 py-2 bg-amber-50 hover:bg-amber-100 text-amber-600 border border-amber-200 font-bold rounded-lg text-xs transition-colors"
                >
                  Force Re-open Case
                </button>
              )}
            </div>
          </div>

          {/* Details Specifications Split Layout */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            
            {/* Split Left: 2-Column Info Grid Card */}
            <div className="md:col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-6">
              
              {/* Columns split */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                
                {/* Col 1: Case Details specifications */}
                <div className="space-y-4">
                  <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest border-b border-slate-100 pb-2">
                    Case Specifications
                  </h3>
                  
                  <div className="space-y-3 text-xs">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400">Lead Registry ID:</span>
                      <strong className="text-slate-800">{selectedCase.id}</strong>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400">Reference:</span>
                      <span className="text-slate-600 font-semibold">{selectedCase.refNumber || 'ADV-2024-00001'}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400">Verification Scheme:</span>
                      <span className="text-blue-600 font-bold">{selectedCase.verificationType || 'Residence Verification'}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400">Client Partner:</span>
                      <span className="text-slate-800 font-medium">{selectedCase.client?.name || 'ICICI Bank'}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400">Mapped Product:</span>
                      <span className="text-slate-600 font-medium">{selectedCase.product?.name || 'Credit Card'}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400">Priority Tier:</span>
                      <span className="text-red-500 font-bold uppercase">{selectedCase.priority || 'Medium'}</span>
                    </div>
                  </div>
                </div>

                {/* Col 2: Applicant contact details */}
                <div className="space-y-4">
                  <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest border-b border-slate-100 pb-2">
                    Applicant Information
                  </h3>
                  
                  <div className="space-y-3 text-xs">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400">Applicant Name:</span>
                      <strong className="text-slate-800">{selectedCase.applicantName}</strong>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400">Phone Contact:</span>
                      <span className="text-slate-700 font-medium flex items-center gap-1">
                        <Phone className="w-3 h-3 text-slate-400" />
                        {selectedCase.applicantPhone || '+91 98765 43210'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400">Alternate Phone:</span>
                      <span className="text-slate-600">{selectedCase.applicantAltPhone || '+91 99988 87766'}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400">Email Address:</span>
                      <span className="text-slate-700 font-medium flex items-center gap-1">
                        <Mail className="w-3 h-3 text-slate-400" />
                        {selectedCase.applicantEmail || 'applicant@email.com'}
                      </span>
                    </div>
                    <div className="flex justify-between items-start">
                      <span className="text-slate-400">Registered Address:</span>
                      <span className="text-slate-700 text-right font-medium max-w-[180px] break-words">
                        {selectedCase.address}
                      </span>
                    </div>
                  </div>
                </div>

              </div>

              {/* Bottom Address details manual tags */}
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                <div>
                  <span className="text-slate-400 block font-medium">City</span>
                  <span className="font-bold text-slate-700 mt-0.5 block">{selectedCase.city || 'Noida'}</span>
                </div>
                <div>
                  <span className="text-slate-400 block font-medium">State</span>
                  <span className="font-bold text-slate-700 mt-0.5 block">{selectedCase.state || 'Uttar Pradesh'}</span>
                </div>
                <div>
                  <span className="text-slate-400 block font-medium">Pincode / Postal Zip</span>
                  <span className="font-bold text-slate-700 mt-0.5 block">{selectedCase.pincode || '201301'}</span>
                </div>
                <div>
                  <span className="text-slate-400 block font-medium">Date Registered</span>
                  <span className="font-bold text-slate-700 mt-0.5 block">
                    {selectedCase.createdAt ? selectedCase.createdAt.substring(0, 10) : '2024-01-15'}
                  </span>
                </div>
              </div>

              {/* Step Guide visual walkthrough */}
              <div className="space-y-3">
                <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wide">
                  Field Operations Workflow Guide
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-[11px]">
                  {[
                    { nr: '1', title: 'Schedule Visit', desc: 'Confirm client contact and trace coordinate telemetry bounds.' },
                    { nr: '2', title: 'Verify Details', desc: 'Fill dynamically loaded outcome questions with field metrics.' },
                    { nr: '3', title: 'Geotag Photos', desc: 'Capture required image evidence watermarked with logo.' },
                    { nr: '4', title: 'QC Audit Submission', desc: 'Commit and sign form to trigger immediate quality checking.' }
                  ].map((step, idx) => (
                    <div key={idx} className="bg-white border border-slate-200 rounded-lg p-3 relative hover:border-blue-200 transition-all">
                      <span className="absolute top-2 right-3 text-slate-200 font-extrabold text-lg select-none">
                        {step.nr}
                      </span>
                      <strong className="text-slate-700 block">{step.title}</strong>
                      <span className="text-slate-400 block mt-1 leading-normal">{step.desc}</span>
                    </div>
                  ))}
                </div>
              </div>

            </div>

            {/* Split Right: Geolocating Telemetry Map Box */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 flex flex-col justify-between">
              
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-slate-800">
                  <Map className="w-5 h-5 text-blue-600" />
                  <h3 className="font-extrabold text-sm">Geocoding Telemetry HUD</h3>
                </div>
                
                {/* Mapped GPS block preview */}
                <div className="h-44 border border-slate-200 rounded-xl relative overflow-hidden bg-slate-50 flex items-center justify-center border-dashed">
                  {/* Grid simulated graphic background */}
                  <div className="absolute inset-0 bg-[linear-gradient(to_right,#e2e8f0_1px,transparent_1px),linear-gradient(to_bottom,#e2e8f0_1px,transparent_1px)] bg-[size:16px_16px] opacity-40"></div>
                  
                  {/* Pin locator in center */}
                  <div className="relative z-10 flex flex-col items-center animate-bounce">
                    <MapPinIcon className="w-8 h-8 text-blue-600 fill-blue-100" />
                    <div className="w-2 h-2 rounded-full bg-blue-600/30 blur-[1px] absolute -bottom-1"></div>
                  </div>
                  
                  {/* Bottom details label bar */}
                  <div className="absolute bottom-2 left-2 right-2 bg-white/95 border border-slate-200 rounded-lg p-2 text-[10px] space-y-0.5 z-10">
                    <div className="flex justify-between">
                      <span className="text-slate-400 font-semibold">Active Coordinates:</span>
                      <span className="text-blue-600 font-bold">23.0225° N, 72.5714° E</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400 font-semibold">Horizontal GPS Accuracy:</span>
                      <span className="text-emerald-600 font-bold">± 4.5 Meters (High)</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-3 text-xs">
                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-100 space-y-1">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                      Geotagged Address Capture
                    </span>
                    <p className="text-slate-600 font-medium leading-normal">
                      {selectedCase.address}
                    </p>
                  </div>

                  <div className="flex items-center gap-2 text-slate-400 text-[10px] font-semibold bg-slate-50 p-2.5 rounded-lg">
                    <Clock className="w-3.5 h-3.5 text-blue-500" />
                    <span>Telemetry ping synchronized: {new Date().toLocaleTimeString()}</span>
                  </div>
                </div>
              </div>

              {/* View Map Action button */}
              <button 
                onClick={() => {
                  setTempLat('23.0225');
                  setTempLng('72.5714');
                  setGeotagModalOpen(true);
                }}
                className="w-full flex items-center justify-center gap-1.5 px-4 py-2 border.5 border-slate-200 text-slate-700 hover:bg-slate-50 transition-all font-bold text-xs rounded-lg mt-6 bg-white"
              >
                <MapPin className="w-4 h-4 text-blue-600" />
                Open Manual Geotag Grid Pin-Editor
              </button>

            </div>

          </div>

        </div>
      )}


      {/* ------------------ SCREEN 3: DYNAMIC VERIFICATION FORM ------------------ */}
      {currentScreen === 3 && selectedCase && (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
          
          {/* Header HUD panel */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setCurrentScreen(2)}
                className="p-2 border border-slate-200 rounded-lg text-slate-500 hover:text-slate-800 hover:bg-slate-50 transition-colors"
                title="Go Back to Case Specifications"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
              
              <div>
                <h1 className="text-base font-extrabold text-slate-800">
                  Form Reporting Workspace - {selectedCase.applicantName}
                </h1>
                <p className="text-xs text-slate-400 mt-0.5">
                  Registry: {selectedCase.id} | Scheme Type: {selectedCase.verificationType || 'Residence Verification'}
                </p>
              </div>
            </div>

            {/* Outcome Switcher controls */}
            <div className="flex items-center border border-slate-200 rounded-lg p-0.5 bg-slate-100">
              <button
                onClick={() => handleOutcomeChange('positive')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition-all ${outcomeMode === 'positive' ? 'bg-white text-emerald-600 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
              >
                <CheckCircle className="w-3.5 h-3.5" />
                Positive Outcome
              </button>
              
              <button
                onClick={() => handleOutcomeChange('negative')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition-all ${outcomeMode === 'negative' ? 'bg-white text-rose-600 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
              >
                <XCircle className="w-3.5 h-3.5" />
                Negative Outcome
              </button>
            </div>
          </div>

          {/* Form Content body split */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            
            {/* Col 1 & 2: Dynamic Form Fields card */}
            <div className="md:col-span-2 space-y-6">
              
              {/* Main Fields card */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-6">
                <div className="flex items-center gap-2 border-b border-slate-100 pb-3 mb-2">
                  <ShieldCheck className="w-5 h-5 text-blue-600" />
                  <div>
                    <h3 className="font-extrabold text-sm text-slate-800">Verification Survey Parameters</h3>
                    <p className="text-[10px] text-slate-400 font-medium">Fields load dynamically based on active outcome schema mapping.</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {activeQuestions.map((q) => {
                    const ansValue = formAnswers[q.id] || '';
                    
                    return (
                      <div 
                        key={q.id} 
                        className={`space-y-1.5 ${q.fieldType === 'textarea' ? 'md:col-span-2' : ''}`}
                      >
                        <label className="text-xs font-bold text-slate-600 flex items-center gap-1">
                          {q.fieldLabel}
                          {q.required && <span className="text-red-500 font-bold">*</span>}
                        </label>

                        {/* Text Field type */}
                        {q.fieldType === 'text' && (
                          <input
                            type="text"
                            value={ansValue}
                            onChange={(e) => handleInputChange(q.id, e.target.value)}
                            className="px-3 py-2 w-full border border-slate-200 rounded-lg text-xs outline-none bg-slate-50 focus:bg-white text-slate-700 font-semibold"
                            placeholder={`Enter ${q.fieldLabel}...`}
                          />
                        )}

                        {/* Number field type */}
                        {q.fieldType === 'number' && (
                          <input
                            type="number"
                            value={ansValue}
                            onChange={(e) => handleInputChange(q.id, e.target.value)}
                            className="px-3 py-2 w-full border border-slate-200 rounded-lg text-xs outline-none bg-slate-50 focus:bg-white text-slate-700 font-semibold"
                            placeholder="0"
                          />
                        )}

                        {/* Date field type */}
                        {q.fieldType === 'date' && (
                          <input
                            type="date"
                            value={ansValue}
                            onChange={(e) => handleInputChange(q.id, e.target.value)}
                            className="px-3 py-2 w-full border border-slate-200 rounded-lg text-xs outline-none bg-slate-50 focus:bg-white text-slate-600 font-semibold cursor-pointer"
                          />
                        )}

                        {/* Textarea field type */}
                        {q.fieldType === 'textarea' && (
                          <textarea
                            value={ansValue}
                            onChange={(e) => handleInputChange(q.id, e.target.value)}
                            rows={3}
                            className="px-3 py-2 w-full border border-slate-200 rounded-lg text-xs outline-none bg-slate-50 focus:bg-white text-slate-700 font-semibold"
                            placeholder={`Provide detailed notes for ${q.fieldLabel.toLowerCase()}...`}
                          />
                        )}

                        {/* Dropdown Select field type */}
                        {q.fieldType === 'dropdown' && (() => {
                          let options = [];
                          try {
                            options = JSON.parse(q.optionsJson || '[]');
                          } catch (e) {
                            options = ['Yes', 'No'];
                          }
                          return (
                            <select
                              value={ansValue}
                              onChange={(e) => handleInputChange(q.id, e.target.value)}
                              className="px-3 py-2 w-full border border-slate-200 rounded-lg text-xs outline-none bg-slate-50 focus:bg-white text-slate-600 font-semibold cursor-pointer"
                            >
                              <option value="">Select option...</option>
                              {options.map((opt, idx) => (
                                <option key={idx} value={opt}>{opt}</option>
                              ))}
                            </select>
                          );
                        })()}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Remarks, internal comments card */}
              <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm space-y-4">
                <div className="border-b border-slate-100 pb-3">
                  <h3 className="font-bold text-slate-800 text-sm">Operation Logs & Remarks</h3>
                </div>

                <div className="space-y-3 text-xs">
                  <div>
                    <label className="block text-slate-500 font-bold mb-1.5">
                      Field Officer Remarks / Summary
                    </label>
                    <textarea
                      value={feRemarks}
                      onChange={(e) => setFeRemarks(e.target.value)}
                      rows={3}
                      className="px-3 py-2 w-full border border-slate-200 rounded-lg text-xs outline-none bg-slate-50 focus:bg-white text-slate-700 font-semibold"
                      placeholder="Add key summary comments gathered during physical visit bounds..."
                    />
                  </div>

                  <div>
                    <label className="block text-slate-500 font-bold mb-1.5">
                      Internal Notes (Invisible to client)
                    </label>
                    <textarea
                      value={internalNotes}
                      onChange={(e) => setInternalNotes(e.target.value)}
                      rows={2}
                      className="px-3 py-2 w-full border border-slate-200 rounded-lg text-xs outline-none bg-slate-50 focus:bg-white text-slate-700 font-semibold"
                      placeholder="Add background notes or telemetry coordinates diagnostics..."
                    />
                  </div>
                </div>
              </div>

              {/* QC Approval remarks panel (Visible to QC role and Admin) */}
              {(currentRole === 'quality_checker' || currentRole === 'admin') && (
                <div className="bg-amber-50/50 border border-amber-200 rounded-xl p-6 shadow-sm space-y-4">
                  <div className="border-b border-amber-200/60 pb-3 flex items-center gap-2">
                    <ShieldCheck className="w-5 h-5 text-amber-600" />
                    <div>
                      <h3 className="font-extrabold text-sm text-slate-800">Quality Checker Review Overlay</h3>
                      <p className="text-[10px] text-slate-500 font-medium">Verify field coordinates accuracy and authorize reports.</p>
                    </div>
                  </div>

                  <div className="space-y-4 text-xs">
                    <div>
                      <span className="block font-bold text-slate-600 mb-1.5">QC Verification Outcome</span>
                      <div className="flex items-center gap-4">
                        <label className="flex items-center gap-2 cursor-pointer font-semibold text-emerald-600 bg-white p-2 rounded-lg border border-slate-200 shadow-sm">
                          <input
                            type="radio"
                            name="qcStatus"
                            checked={qcStatus === 'approve'}
                            onChange={() => setQcStatus('approve')}
                            className="text-emerald-500 focus:ring-emerald-400"
                          />
                          Approve Report (Success)
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer font-semibold text-rose-600 bg-white p-2 rounded-lg border border-slate-200 shadow-sm">
                          <input
                            type="radio"
                            name="qcStatus"
                            checked={qcStatus === 'reject'}
                            onChange={() => setQcStatus('reject')}
                            className="text-rose-500 focus:ring-rose-400"
                          />
                          Reject Verification (Needs Revisit)
                        </label>
                      </div>
                    </div>

                    <div>
                      <label className="block font-bold text-slate-600 mb-1.5">QC Audit Comments</label>
                      <textarea
                        value={qcRemarks}
                        onChange={(e) => setQcRemarks(e.target.value)}
                        rows={3}
                        className="px-3 py-2 w-full border border-slate-200 rounded-lg text-xs bg-white text-slate-700 outline-none focus:ring-1 focus:ring-amber-400 font-semibold shadow-sm"
                        placeholder="Add QC remarks concerning evidence alignment, neighbor statements review..."
                      />
                    </div>
                  </div>
                </div>
              )}

            </div>

            {/* Col 3: Photo Evidence Gallery with geotag details */}
            <div className="space-y-6">
              
              {/* Media Gallery HUD box */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-4">
                <div className="border-b border-slate-100 pb-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Camera className="w-5 h-5 text-blue-600" />
                    <h3 className="font-extrabold text-sm text-slate-800">Photo Evidence</h3>
                  </div>
                  <span className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded font-bold">
                    {uploadedImages.filter(i => i.previewUrl).length} / {uploadedImages.length}
                  </span>
                </div>

                <div className="space-y-4">
                  {uploadedImages.map((img, idx) => (
                    <div 
                      key={idx} 
                      className="border border-slate-200 rounded-xl p-3 bg-slate-50 space-y-3"
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <strong className="text-xs text-slate-700 block leading-tight">{img.label}</strong>
                          <span className="text-[10px] text-slate-400 mt-0.5 block">
                            {img.required ? 'Mandatory Field' : 'Optional Evidence'}
                          </span>
                        </div>
                        {img.previewUrl && (
                          <button
                            onClick={() => removeImage(idx)}
                            className="p-1 border border-slate-200 hover:border-red-200 text-slate-400 hover:text-red-500 rounded bg-white transition-colors"
                            title="Delete Image"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>

                      {/* Preview or capture placeholder box */}
                      {img.previewUrl ? (
                        <div className="relative border border-slate-200 rounded-lg overflow-hidden h-36 bg-slate-800">
                          {/* Image preview */}
                          <img 
                            src={img.previewUrl} 
                            alt={img.label} 
                            className="w-full h-full object-cover opacity-85" 
                          />
                          
                          {/* Watermarked EXIF specs layer overlay */}
                          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/95 via-transparent to-transparent flex flex-col justify-end p-2.5 space-y-0.5 select-none text-[8px] text-slate-300 font-medium">
                            <div className="flex justify-between text-[7px] text-blue-400 font-bold border-b border-slate-800/40 pb-0.5">
                              <span>AD VENTURES WATERMARK VERIFIED</span>
                              <span>GPS LEVEL A</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Coordinates:</span>
                              <span className="text-white font-bold">{img.lat}° N, {img.lng}° E</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Timestamp:</span>
                              <span className="text-white">{img.timestamp}</span>
                            </div>
                            <div className="flex justify-between truncate">
                              <span>Location:</span>
                              <span className="text-white max-w-[130px] truncate">{img.address}</span>
                            </div>
                          </div>
                          
                          {/* Pin-point Geotag overlay button */}
                          {img.geoTagRequired && (
                            <button
                              onClick={() => openGeotagModal(idx)}
                              className="absolute top-2 right-2 bg-white/90 hover:bg-white text-slate-800 border border-slate-200 rounded p-1.5 shadow-sm text-[9px] font-bold flex items-center gap-1 transition-all"
                              title="Manual GPS Tagger"
                            >
                              <MapPin className="w-3 h-3 text-blue-600" />
                              Edit Geo Specs
                            </button>
                          )}
                        </div>
                      ) : (
                        <div className="border border-slate-200 border-dashed rounded-lg p-6 bg-white flex flex-col items-center justify-center gap-3 text-center">
                          <Camera className="w-8 h-8 text-slate-300" />
                          <div>
                            <span className="text-[10px] text-slate-400 font-semibold block leading-tight">
                              Drag and drop picture here
                            </span>
                            <span className="text-[9px] text-slate-300 block mt-0.5">
                              JPEG, PNG up to 10MB
                            </span>
                          </div>
                          
                          <div className="flex gap-2 w-full">
                            {/* Upload Simulator button */}
                            <label className="flex-1 flex items-center justify-center gap-1 py-1.5 border border-slate-200 text-[10px] font-bold rounded bg-slate-50 hover:bg-slate-100 cursor-pointer text-slate-600">
                              <Upload className="w-3.5 h-3.5" />
                              Browse File
                              <input 
                                type="file" 
                                onChange={(e) => handleFileUpload(idx, e)} 
                                className="hidden" 
                                accept="image/*"
                              />
                            </label>
                            
                            {/* Camera Simulator button */}
                            <button
                              onClick={() => handleCameraCapture(idx)}
                              className="flex-1 flex items-center justify-center gap-1 py-1.5 border border-transparent text-[10px] font-bold rounded bg-blue-600 text-white hover:bg-blue-700 transition-colors shadow-sm shadow-blue-100"
                            >
                              <Camera className="w-3.5 h-3.5" />
                              Capture Pic
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Document Drawer list */}
              <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm space-y-4">
                <div className="border-b border-slate-100 pb-3 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-600" />
                  <h3 className="font-extrabold text-sm text-slate-800">Support Documents</h3>
                </div>

                <div className="space-y-2 text-xs">
                  {selectedCase.documents && selectedCase.documents.length > 0 ? (
                    selectedCase.documents.map((doc, idx) => (
                      <div key={idx} className="flex justify-between items-center p-2.5 bg-slate-50 border border-slate-100 rounded-lg hover:border-slate-200 transition-colors">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-blue-600" />
                          <div>
                            <span className="font-bold text-slate-700 block truncate max-w-[150px]" title={doc.name}>
                              {doc.name}
                            </span>
                            <span className="text-[10px] text-slate-400">{doc.size || '256 KB'}</span>
                          </div>
                        </div>
                        <button className="text-[10px] font-bold text-blue-600 hover:text-blue-800 bg-white border border-slate-200 rounded p-1 px-2 hover:border-slate-300 shadow-sm transition-all">
                          Inspect File
                        </button>
                      </div>
                    ))
                  ) : (
                    <div className="text-center p-4 bg-slate-50 rounded-lg border border-slate-100">
                      <span className="text-[10px] text-slate-400 font-semibold block leading-tight">
                        No support docs attached to case.
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Submit / Draft Panel Cards */}
              <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm grid grid-cols-2 gap-3">
                <button
                  onClick={handleSaveDraft}
                  className="flex items-center justify-center gap-1.5 py-2.5 border border-slate-200 hover:bg-slate-50 text-slate-700 font-bold rounded-lg text-xs transition-colors shadow-sm bg-white"
                >
                  <Save className="w-3.5 h-3.5" />
                  Save Draft State
                </button>
                
                <button
                  onClick={handleSubmitVerification}
                  className="flex items-center justify-center gap-1.5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold transition-colors shadow-sm shadow-emerald-100"
                >
                  <FileCheck className="w-3.5 h-3.5" />
                  {currentRole === 'quality_checker' ? 'Verify Outcome' : 'Submit for QC review'}
                </button>
              </div>

            </div>

          </div>

        </div>
      )}


      {/* ------------------ MANUAL GEOTAGGING MODAL ------------------ */}
      {geotagModalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl border border-slate-200 max-w-lg w-full overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200 flex flex-col justify-between">
            
            {/* Modal Header */}
            <div className="bg-white border-b border-slate-100 p-5 px-6 flex justify-between items-center">
              <div>
                <h3 className="text-base font-extrabold text-slate-800">
                  Manual Telemetry GPS Editor
                </h3>
                <p className="text-[10px] text-slate-400 font-medium">
                  Type latitude/longitude parameters or click on map visualizer to resolve custom bounds.
                </p>
              </div>
              <button 
                onClick={() => setGeotagModalOpen(false)}
                className="p-1 border border-slate-200 hover:bg-slate-50 text-slate-400 hover:text-slate-700 rounded-lg transition-colors bg-white"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-6">
              
              {/* Interactive map visualizer grid */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-500 block">
                  Interactive Grid Mapped Coordinates Plane (Click to set pin location)
                </label>
                <div 
                  onClick={handleMapGridClick}
                  className="h-48 border border-slate-200 rounded-xl relative overflow-hidden bg-slate-50 flex items-center justify-center border-dashed cursor-crosshair group shadow-inner"
                >
                  {/* Grid simulated graphic background */}
                  <div className="absolute inset-0 bg-[linear-gradient(to_right,#cbd5e1_1px,transparent_1px),linear-gradient(to_bottom,#cbd5e1_1px,transparent_1px)] bg-[size:16px_16px] opacity-25"></div>
                  
                  {/* Outer circle visual radar */}
                  <div className="absolute w-40 h-40 rounded-full border border-blue-200/50 bg-blue-50/10 z-0"></div>
                  <div className="absolute w-20 h-20 rounded-full border border-blue-200 bg-blue-50/20 z-0"></div>

                  {/* Pin locator representation based on coordinates */}
                  <div className="relative z-10 flex flex-col items-center select-none group-hover:scale-105 transition-transform duration-200">
                    <MapPinIcon className="w-9 h-9 text-blue-600 fill-blue-100" />
                    <div className="w-2.5 h-2.5 rounded-full bg-blue-600/30 blur-[1px] absolute -bottom-1"></div>
                  </div>

                  <span className="absolute top-2 right-3 text-[9px] bg-slate-200/80 border border-slate-300 text-slate-600 px-2 py-0.5 rounded font-semibold select-none z-10">
                    Map Offset Simulator
                  </span>
                </div>
              </div>

              {/* Text Input inputs for Coordinates */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-500 block">
                    Latitude Coordinate
                  </label>
                  <input
                    type="number"
                    step="0.000001"
                    value={tempLat}
                    onChange={(e) => setTempLat(e.target.value)}
                    className="px-3 py-2 w-full border border-slate-200 rounded-lg text-xs outline-none bg-slate-50 focus:bg-white text-slate-700 font-bold"
                    placeholder="23.0225"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-500 block">
                    Longitude Coordinate
                  </label>
                  <input
                    type="number"
                    step="0.000001"
                    value={tempLng}
                    onChange={(e) => setTempLng(e.target.value)}
                    className="px-3 py-2 w-full border border-slate-200 rounded-lg text-xs outline-none bg-slate-50 focus:bg-white text-slate-700 font-bold"
                    placeholder="72.5714"
                  />
                </div>
              </div>

              <div className="p-3 bg-blue-50/50 rounded-xl border border-blue-100 flex items-start gap-2.5 text-xs leading-normal">
                <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="text-blue-700 font-medium">
                  <strong>Telemetry Specs Note:</strong> These custom geocoded coordinates are watermarked permanently onto photo evidence watermarking overlays to meet strict compliance audits.
                </div>
              </div>

            </div>

            {/* Modal Footer */}
            <div className="bg-slate-50 border-t border-slate-100 p-4 px-6 flex justify-end gap-3">
              <button
                onClick={() => setGeotagModalOpen(false)}
                className="px-4 py-2 border border-slate-200 hover:bg-slate-100 text-slate-700 font-semibold rounded-lg text-xs transition-colors bg-white shadow-sm"
              >
                Discard
              </button>
              
              <button
                onClick={saveGeotagDetails}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-extrabold transition-colors shadow-sm shadow-blue-100"
              >
                Apply Coordinates
              </button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
};

export default ManualVerification;
